import { useMemo, useRef, useState } from 'react';
import { formatBytes, unfilledPlaceholders } from '@wa/shared';
import { QuickReplyPanel, type QuickReplyAttachment, type QuickReplyKeys } from '@/components/QuickReplyPanel';
import { SchedulePanel } from '@/components/SchedulePanel';
import type { ScheduledMessage } from '@wa/shared';

interface Props {
  /** Resolves {{name}} in a quick reply. */
  contactName: string | null;
  /** For a quick reply's {{field.key}}. */
  contactId?: number | null;
  isSending: boolean;
  isUploading: boolean;
  /** Resolves when sent; rejects when not, and the composer puts the text back. */
  onSend: (body: string) => Promise<unknown> | void;
  /** The same send with a date on it — see the F16 section of the build reference. */
  onSchedule: (body: string, whenUtcIso: string) => void;
  onCancelScheduled: (id: number) => void;
  scheduled: ScheduledMessage[];
  windowExpiresAt: string | null;
  onSendMedia: (file: File, caption: string) => Promise<unknown> | void;
  /** A file already uploaded — a quick reply's attachment — by asset id. */
  onSendStoredMedia?: (mediaAssetId: number, caption: string) => Promise<unknown> | void;
  onOpenTemplates: () => void;
  /** Opens the product picker — the catalogue into the chat. */
  onOpenProducts: () => void;
}

/** WhatsApp will not accept more than this, so the browser refuses it first. */
const MAX_BYTES = 16 * 1024 * 1024;

/**
 * Per send. The Cloud API has no album message — every file is its own message,
 * its own outbox row and its own receipt — so this is a run of separate sends,
 * and a run of thirty from a business number is what gets a number reported.
 * WhatsApp's own picker stops at thirty; ten is plenty for a support reply.
 */
const MAX_FILES = 10;

export function Composer({
  contactName, contactId, isSending, isUploading, onSend, onSendMedia, onSendStoredMedia, onOpenTemplates, onOpenProducts,
  onSchedule, onCancelScheduled, scheduled, windowExpiresAt,
}: Props) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  // A quick reply's file: already at Meta, referenced by id, sent with the text
  // as its caption. Kept apart from `attachments` because nothing is uploaded.
  const [stored, setStored] = useState<QuickReplyAttachment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quickRepliesOff, setQuickRepliesOff] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const quickReplies = useRef<QuickReplyKeys>(null);

  /**
   * The panel opens only when the slash is the very first character.
   *
   * Firing on any slash would open it in the middle of every URL an agent pastes
   * — "https://" alone would trigger it twice. A quick reply is the whole
   * message rather than a fragment of one, so the first character is where it
   * belongs.
   */
  const quickReplyQuery = useMemo(() => {
    if (quickRepliesOff || attachments.length > 0 || stored) return null;
    if (!draft.startsWith('/')) return null;

    const upto = draft.slice(1);
    // A newline means they have moved on and meant a literal slash.
    return upto.includes('\n') ? null : upto;
  }, [draft, quickRepliesOff, attachments.length, stored]);

  // Placeholders the quick reply could not fill; the agent types those.
  const unfilled = useMemo(() => unfilledPlaceholders(draft), [draft]);

  // True for the whole run of a multi-file send. The parent's isUploading drops
  // between files, and the box must not reopen in that gap.
  const [inRun, setInRun] = useState(false);
  const busy = isSending || isUploading || inRun;

  function choose(picked: FileList | null) {
    if (!picked || picked.length === 0) return;

    // Rejected here rather than after a slow upload that was always going to fail.
    const tooBig = Array.from(picked).filter((f) => f.size > MAX_BYTES);
    const fits = Array.from(picked).filter((f) => f.size <= MAX_BYTES);

    const problems: string[] = [];
    if (tooBig.length > 0) {
      problems.push(`${tooBig.map((f) => `${f.name} (${formatBytes(f.size)})`).join(', ')} left out — WhatsApp accepts up to 16 MB each.`);
    }

    setAttachments((current) => {
      const room = MAX_FILES - current.length;
      if (fits.length > room) {
        problems.push(`Up to ${MAX_FILES} files in one go; the first ${Math.max(room, 0)} were kept.`);
      }
      return [...current, ...fits.slice(0, Math.max(room, 0))];
    });

    setError(problems.length > 0 ? problems.join(' ') : null);
  }

  function insertQuickReply(text: string, attachment: QuickReplyAttachment | null) {
    setDraft(text);
    setQuickRepliesOff(true);
    if (attachment) { setStored(attachment); setAttachments([]); }

    // The caret goes to the first placeholder left unfilled, selected, so the
    // agent's next keystroke replaces it; otherwise to the end.
    queueMicrotask(() => {
      box.current?.focus();
      const open = text.search(/\{\{\s*[a-zA-Z_][\w.-]*\s*\}\}/);
      if (open >= 0) {
        const close = text.indexOf('}}', open) + 2;
        box.current?.setSelectionRange(open, close);
      } else {
        box.current?.setSelectionRange(text.length, text.length);
      }
    });
  }

  function submit() {
    if (busy) return;

    // A placeholder still in the text is a message that reads "{{order_no}}" to
    // the customer. Refused here, with the box focused on it, rather than sent.
    if (unfilled.length > 0) {
      setError(`Fill in {{${unfilled[0]}}} before sending.`);
      const open = draft.search(/\{\{/);
      box.current?.focus();
      box.current?.setSelectionRange(open, draft.indexOf('}}', open) + 2);
      return;
    }

    if (stored && onSendStoredMedia) {
      const file = stored;
      const caption = draft.trim();
      setStored(null);
      setDraft('');
      Promise.resolve(onSendStoredMedia(file.mediaAssetId, caption)).catch(() => {
        setStored((current) => current ?? file);
        setDraft((current) => (current === '' ? caption : current));
      });
      return;
    }

    // Cleared at once, as a chat app does, and restored if the send fails and
    // the agent has not started something new — a 402 or a closed window used
    // to cost them the paragraph they had just written.
    if (attachments.length > 0) {
      const files = attachments;
      const caption = draft.trim();
      setAttachments([]);
      setDraft('');

      // One message per file, in the order they were picked, each sent only
      // once the previous one was accepted: the outbox sends a number's rows in
      // id order, so what the customer sees is the order the agent chose. The
      // caption rides on the first, as WhatsApp does. A rejection part-way
      // through puts back the file that failed and everything after it, not the
      // ones already queued.
      setInRun(true);
      void (async () => {
        try {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;
            try {
              await onSendMedia(file, i === 0 ? caption : '');
            } catch {
              const rest = files.slice(i);
              setAttachments((current) => (current.length === 0 ? rest : current));
              if (i === 0) setDraft((current) => (current === '' ? caption : current));
              return;
            }
          }
        } finally {
          setInRun(false);
        }
      })();
      return;
    }

    const body = draft.trim();
    if (!body) return;
    setDraft('');
    setQuickRepliesOff(false);
    Promise.resolve(onSend(body)).catch(() => {
      setDraft((current) => (current === '' ? body : current));
    });
  }

  // The closed-window state lives in SessionOpener now, one level up: the thread
  // renders that INSTEAD of this composer rather than this composer rendering a
  // banner above itself. A free-form box behind a notice saying free-form is
  // refused is a box that exists to be typed into and then rejected.

  return (
    <div className="relative mx-auto flex max-w-3xl flex-col gap-2">

      {(showSchedule || scheduled.length > 0) && (
        <SchedulePanel
          windowExpiresAt={windowExpiresAt}
          pending={scheduled}
          isBusy={busy}
          onSchedule={(when) => {
            const body = draft.trim();
            if (!body) return;
            onSchedule(body, when);
            setDraft('');
            setShowSchedule(false);
          }}
          onCancel={onCancelScheduled}
          onClose={() => setShowSchedule(false)}
        />
      )}

      <QuickReplyPanel
        ref={quickReplies}
        query={quickReplyQuery}
        contactName={contactName}
        contactId={contactId}
        draft={draft}
        onPick={insertQuickReply}
        onClose={() => setQuickRepliesOff(true)}
      />

      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {stored && (
        <div className="flex items-center gap-2 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-small font-medium">{stored.fileName}</span>
            <span className="block text-micro text-(--color-muted)">From the quick reply — sent with the text as its caption</span>
          </span>
          <button
            type="button"
            onClick={() => setStored(null)}
            disabled={busy}
            className="tap grid shrink-0 place-items-center rounded px-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-50"
            aria-label="Remove attachment"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {attachments.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2" aria-label="Attachments">
          {attachments.map((file, index) => (
            <li key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-small font-medium">{file.name}</span>
                <span className="block text-micro text-(--color-muted)">{formatBytes(file.size)}</span>
              </span>
              <button
                type="button"
                onClick={() => setAttachments((current) => current.filter((_, i) => i !== index))}
                disabled={busy}
                className="tap grid shrink-0 place-items-center rounded px-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-50"
                aria-label={`Remove ${file.name}`}
              >
                <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
          {attachments.length > 1 && (
            <li className="text-micro text-(--color-muted)">
              {attachments.length} files — sent as {attachments.length} messages, in this order. The caption goes on the first.
            </li>
          )}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={picker}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={(event) => {
            choose(event.target.files);
            event.target.value = '';
          }}
        />

        <button
          type="button"
          onClick={() => picker.current?.click()}
          disabled={busy}
          className="tap grid shrink-0 place-items-center rounded-lg border border-(--color-line) px-3 py-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-40"
          aria-label="Attach a file"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M13.5 6.5 8 12a2.1 2.1 0 0 0 3 3l5.5-5.5a4.2 4.2 0 0 0-6-6L5 9a6.3 6.3 0 0 0 9 9l4-4"
                  fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          onClick={onOpenTemplates}
          disabled={busy}
          className="tap grid shrink-0 place-items-center rounded-lg border border-(--color-line) px-3 py-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-40"
          aria-label="Send a template"
          title="Send a template"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <rect x="3" y="4" width="14" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 8h14M7 11.5h6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <button
          type="button"
          onClick={onOpenProducts}
          disabled={busy}
          className="tap grid shrink-0 place-items-center rounded-lg border border-(--color-line) px-3 py-2 text-(--color-muted) hover:text-(--color-ink) disabled:opacity-40"
          aria-label="Send products"
          title="Send products from the catalogue"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M4 7h12l-1 9H5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M7.5 7V5.5a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {/* Only offered once there is something to schedule. An empty draft with a
            date on it is not a message, and the button would be a dead end. */}
        <button
          type="button"
          onClick={() => setShowSchedule((v) => !v)}
          disabled={busy || (!draft.trim() && !showSchedule)}
          aria-label="Send later"
          aria-expanded={showSchedule}
          className="tap grid shrink-0 place-items-center rounded-lg px-1.5 text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-40"
        >
          <svg viewBox="0 0 20 20" width="19" height="19" aria-hidden="true">
            <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10 6v4.3l2.8 1.7" fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <textarea
          ref={box}
          rows={1}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            // Clearing the box re-arms the panel, so a second slash works.
            if (!event.target.value.startsWith('/')) setQuickRepliesOff(false);
          }}
          onKeyDown={(event) => {
            // Escape closes the panel and leaves the draft alone: someone who
            // typed a literal slash must not have to delete anything.
            if (event.key === 'Escape' && quickReplyQuery !== null) {
              event.preventDefault();
              setQuickRepliesOff(true);
              return;
            }

            // While the panel is open the arrows move its highlight and Enter
            // picks it. Before this, Enter with "/gree" on screen and "greeting"
            // drawn as chosen sent the literal "/gree" to the customer.
            if (quickReplyQuery !== null) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                quickReplies.current?.move(event.key === 'ArrowDown' ? 1 : -1);
                return;
              }
              if (event.key === 'Enter' && !event.shiftKey && quickReplies.current?.pick()) {
                event.preventDefault();
                return;
              }
            }

            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={attachments.length > 1 ? 'Caption for the first file (optional)' : attachments.length === 1 || stored ? 'Add a caption (optional)' : 'Write a reply'}
          disabled={busy}
          aria-label="Reply"
          className="tap max-h-40 min-h-10 min-w-0 flex-1 resize-y rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand) disabled:opacity-60"
        />

        <button
          type="button"
          onClick={submit}
          disabled={busy || (!draft.trim() && attachments.length === 0 && !stored)}
          className="tap grid shrink-0 place-items-center rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) disabled:opacity-40"
        >
          {isUploading ? 'Uploading…' : isSending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
